import {
  Activity,
  BellRing,
  CalendarRange,
  ChartColumn,
  ExternalLink,
  Eye,
  Film,
  Flame,
  FileText,
  Inbox as InboxIcon,
  LayoutDashboard,
  ListChecks,
  ListVideo,
  Mic,
  Mic2,
  MonitorPlay,
  Layers,
  Newspaper,
  Radio,
  RadioTower,
  Settings,
  SplitSquareHorizontal,
  Users,
  X,
} from 'lucide-react';
import type { ComponentType } from 'react';
import { NavLink } from 'react-router-dom';
import { useInbox } from '../lib/api/inbox';
import { useAuthStore } from '../lib/auth/store';
import { useSidebarStore } from '../lib/sidebarStore';
import { cn } from '../lib/utils';
import { Logo } from './Logo';
import { RadioPlayer } from './RadioPlayer';

interface NavItem {
  to: string;
  label: string;
  icon: ComponentType<{ className?: string }>;
  /** Show only when role matches. `undefined` = visible to any signed-in user. */
  requiredRole?: 'admin';
}

const NAV_GROUPS: { heading?: string; items: NavItem[] }[] = [
  {
    items: [
      { to: '/', label: 'Dashboard', icon: LayoutDashboard },
      { to: '/inbox', label: 'Inbox', icon: InboxIcon },
    ],
  },
  {
    heading: 'Newsroom',
    items: [
      { to: '/posts', label: 'Posts', icon: Newspaper },
      { to: '/pages', label: 'Pages', icon: FileText },
      { to: '/breaking', label: 'Breaking news', icon: BellRing, requiredRole: 'admin' },
      { to: '/news-desk', label: 'News desk', icon: SplitSquareHorizontal, requiredRole: 'admin' },
      { to: '/stories', label: 'Stories', icon: Layers, requiredRole: 'admin' },
      { to: '/eyewitness', label: 'Eyewitness', icon: Eye, requiredRole: 'admin' },
    ],
  },
  {
    heading: 'Broadcasting',
    items: [
      { to: '/channels', label: 'Channels', icon: Radio, requiredRole: 'admin' },
      { to: '/programmes', label: 'Programmes', icon: Mic, requiredRole: 'admin' },
      { to: '/now-playing', label: 'Now playing', icon: MonitorPlay, requiredRole: 'admin' },
      { to: '/live-contributions', label: 'Live contributions', icon: Radio, requiredRole: 'admin' },
      { to: '/broadcasts', label: 'Broadcasts', icon: RadioTower, requiredRole: 'admin' },
      { to: '/live-contributions/archive', label: 'Recordings archive', icon: Film, requiredRole: 'admin' },
      { to: '/playlists', label: 'Playlists', icon: ListVideo, requiredRole: 'admin' },
      { to: '/schedule', label: 'Schedule', icon: CalendarRange, requiredRole: 'admin' },
      { to: '/presenter-brief', label: 'Presenter brief', icon: Mic2, requiredRole: 'admin' },
    ],
  },
  {
    heading: 'Audience',
    items: [
      { to: '/analytics', label: 'Analytics', icon: ChartColumn, requiredRole: 'admin' },
      { to: '/events', label: 'Events', icon: ListChecks, requiredRole: 'admin' },
      { to: '/users', label: 'Users', icon: Users, requiredRole: 'admin' },
    ],
  },
  {
    heading: 'System',
    items: [
      { to: '/activity', label: 'Activity log', icon: Activity, requiredRole: 'admin' },
      { to: '/settings', label: 'Settings', icon: Settings, requiredRole: 'admin' },
    ],
  },
];

export function Sidebar(): JSX.Element {
  const role = useAuthStore((s) => s.user?.role);
  const { open, close } = useSidebarStore();

  return (
    <>
      {/* Desktop sidebar — sticky so it stays glued to the viewport while
       *  the main column scrolls. `h-screen` caps it at viewport height so
       *  the inner `SidebarNav`'s `overflow-y-auto` handles long nav lists
       *  without forcing the whole page to scroll. `self-start` is the
       *  flex-equivalent of "don't stretch to match siblings" — without
       *  it the parent flex stretches the aside to the full document
       *  height and the sticky positioning has no room to operate. */}
      <aside className="sticky top-0 hidden h-screen w-64 shrink-0 flex-col self-start border-r border-line bg-surface md:flex">
        {/* Vertical Nigerian flag tricolor spine — quiet but unmistakably national. */}
        <div className="tricolor-spine pointer-events-none absolute inset-y-0 left-0 w-[3px]" aria-hidden />
        <div className="flex h-16 items-center border-b border-line pl-5 pr-4">
          <Logo withWordmark />
        </div>
        <SidebarNav role={role} />
        <RadioPlayer />
      </aside>

      {/* Mobile drawer */}
      {open ? (
        <>
          <div
            className="fixed inset-0 z-40 bg-brand-deep/50 backdrop-blur-sm md:hidden"
            onClick={close}
            aria-hidden
          />
          <aside className="fixed inset-y-0 left-0 z-50 flex w-72 flex-col border-r border-line bg-surface md:hidden">
            <div className="tricolor-spine pointer-events-none absolute inset-y-0 left-0 w-[3px]" aria-hidden />
            <div className="flex h-16 items-center justify-between border-b border-line pl-5 pr-3">
              <Logo withWordmark />
              <button
                type="button"
                onClick={close}
                className="rounded-md p-1.5 text-ink-subtle transition-colors hover:bg-surface-muted hover:text-ink"
                aria-label="Close menu"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <SidebarNav role={role} onNavigate={close} />
            <RadioPlayer />
          </aside>
        </>
      ) : null}
    </>
  );
}

function SidebarNav({
  role,
  onNavigate,
}: {
  role: string | undefined;
  onNavigate?: () => void;
}): JSX.Element {
  // Drives the count badge on the Inbox nav item. Polls every 30s on its own
  // — same hook as the /inbox screen, so React Query dedupes the request to
  // a single fetch even when both are mounted.
  const inboxQ = useInbox();
  const inboxCount = inboxQ.data?.totalCount ?? 0;

  return (
    <nav className="flex-1 overflow-y-auto px-3 py-5">
      {NAV_GROUPS.map((group, groupIdx) => {
        const visible = group.items.filter((it) => !it.requiredRole || role === it.requiredRole);
        if (visible.length === 0) return null;
        const groupKey = group.heading ?? group.items[0]?.to ?? 'group';
        return (
          <div key={groupKey} className={cn('mb-5', groupIdx === 0 && 'mb-3')}>
            {group.heading ? (
              <div className="mb-1.5 flex items-center gap-2 px-2.5">
                <span className="font-mono text-[9.5px] font-semibold uppercase tracking-[0.22em] text-ink-subtle">
                  {group.heading}
                </span>
                <span className="h-px flex-1 bg-line-hair" />
              </div>
            ) : null}
            <ul className="space-y-0.5">
              {visible.map((item) => {
                const showCount = item.to === '/inbox' && inboxCount > 0;
                return (
                  <li key={item.to}>
                    <NavLink
                      to={item.to}
                      // Use exact matching when this item's path is a
                      // strict prefix of another visible sidebar item
                      // — otherwise React Router's default prefix
                      // match highlights *both* `/live-contributions`
                      // and `/live-contributions/archive` when the
                      // user is on the archive page. Root `/` always
                      // needs `end` to avoid matching everything.
                      end={
                        item.to === '/' ||
                        visible.some(
                          (other) => other.to !== item.to && other.to.startsWith(`${item.to}/`),
                        )
                      }
                      onClick={onNavigate}
                      className={({ isActive }) =>
                        cn(
                          'group relative flex items-center gap-2.5 rounded-md py-2 pl-3 pr-2.5 text-[13.5px] font-medium transition-all duration-150',
                          isActive
                            ? 'bg-brand-soft text-brand'
                            : 'text-ink-muted hover:bg-surface-muted hover:text-ink',
                        )
                      }
                    >
                      {({ isActive }) => (
                        <>
                          {/* Active rail — a short brand-colored bar at the left edge */}
                          <span
                            aria-hidden
                            className={cn(
                              'pointer-events-none absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-r bg-brand transition-all duration-150',
                              isActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-30',
                            )}
                          />
                          <item.icon
                            className={cn(
                              'h-[15px] w-[15px] transition-colors',
                              isActive ? 'text-brand' : 'text-ink-subtle group-hover:text-ink',
                            )}
                          />
                          <span className="flex-1 truncate text-ink">{item.label}</span>
                          {showCount ? (
                            <span
                              className={cn(
                                'ml-auto inline-flex h-5 min-w-[1.25rem] shrink-0 items-center justify-center rounded-full px-1.5 font-mono text-[10.5px] font-semibold tabular-nums',
                                isActive
                                  ? 'bg-brand text-white'
                                  : 'bg-brand text-white group-hover:bg-brand-hover',
                              )}
                              aria-label={`${inboxCount} inbox items`}
                            >
                              {inboxCount > 99 ? '99+' : inboxCount}
                            </span>
                          ) : null}
                        </>
                      )}
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}
      {role === 'admin' ? (
        <div className="mb-5">
          <div className="mb-1.5 flex items-center gap-2 px-2.5">
            <span className="font-mono text-[9.5px] font-semibold uppercase tracking-[0.22em] text-ink-subtle">
              External
            </span>
            <span className="h-px flex-1 bg-line-hair" />
          </div>
          <a
            href="https://console.firebase.google.com/project/voice-of-nigeria/analytics"
            target="_blank"
            rel="noopener noreferrer"
            onClick={onNavigate}
            className="group relative flex items-center gap-2.5 rounded-md py-2 pl-3 pr-2.5 text-[13.5px] font-medium text-ink-muted transition-all duration-150 hover:bg-surface-muted hover:text-ink"
          >
            <Flame className="h-[15px] w-[15px] text-ink-subtle group-hover:text-ink" />
            <span className="flex-1 truncate text-ink">Firebase Console</span>
            <ExternalLink className="h-3 w-3 text-ink-subtle group-hover:text-ink" />
          </a>
        </div>
      ) : null}
    </nav>
  );
}

